import { useEffect, useRef, useCallback, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useEditorStore } from '../store/editor.store'

export interface Peer {
  clientId: string
  userId: string
  userName: string
  color: string
  cursor?: { x: number; y: number }
  selection?: string | null
}

type PeersMap = Map<string, Peer>

export function useCollab(siteId: string | null) {
  const socketRef = useRef<Socket | null>(null)
  const peersRef = useRef<PeersMap>(new Map())
  const versionRef = useRef(0)
  const isApplyingRemote = useRef(false)
  const prevPageRef = useRef(useEditorStore.getState().page)

  const [peers, setPeers] = useState<Peer[]>([])

  const store = useEditorStore

  function syncPeers() {
    setPeers(Array.from(peersRef.current.values()))
  }

  const broadcastBlockUpdate = useCallback((sectionId: string, blockId: string, data: unknown) => {
    const socket = socketRef.current
    if (!socket?.connected) return
    versionRef.current++
    socket.emit('block:update', { sectionId, blockId, data, version: versionRef.current })
  }, [])

  const broadcastCursor = useCallback((x: number, y: number) => {
    socketRef.current?.emit('cursor:move', { x, y })
  }, [])

  const broadcastSelection = useCallback((blockId: string | null) => {
    socketRef.current?.emit('selection:change', { blockId })
  }, [])

  // Subscribe to store and broadcast local block changes to peers
  useEffect(() => {
    if (!siteId) return
    return store.subscribe((state) => {
      if (isApplyingRemote.current) return
      const prev = prevPageRef.current
      const next = state.page
      if (prev === next) return
      prevPageRef.current = next

      for (const sec of next.sections) {
        const prevSec = prev.sections.find((s) => s.id === sec.id)
        if (!prevSec) continue
        for (const block of sec.blocks) {
          const prevBlock = prevSec.blocks.find((b) => b.id === block.id)
          if (!prevBlock) continue
          if (prevBlock.data !== block.data) {
            broadcastBlockUpdate(sec.id, block.id, block.data)
          }
        }
      }
    })
  }, [siteId, store, broadcastBlockUpdate])

  useEffect(() => {
    if (!siteId) return
    const token = localStorage.getItem('bst-token')
    if (!token) return

    const socket = io('/collab', {
      auth: { token },
      query: { siteId },
      transports: ['websocket'],
    })

    socketRef.current = socket

    socket.on('peers:list', (list: Peer[]) => {
      peersRef.current = new Map(list.map(p => [p.clientId, p]))
      syncPeers()
    })

    socket.on('peer:join', (peer: Peer) => {
      peersRef.current.set(peer.clientId, peer)
      syncPeers()
    })

    socket.on('peer:leave', ({ clientId }: { clientId: string }) => {
      peersRef.current.delete(clientId)
      syncPeers()
    })

    socket.on('cursor:move', (data: { clientId: string; color: string; userName: string; x: number; y: number }) => {
      const peer = peersRef.current.get(data.clientId)
      if (peer) {
        peer.cursor = { x: data.x, y: data.y }
        window.dispatchEvent(new CustomEvent('bst:cursor', { detail: { clientId: data.clientId, color: data.color, userName: data.userName, x: data.x, y: data.y } }))
      }
    })

    socket.on('selection:change', (data: { clientId: string; color: string; userName: string; blockId: string | null }) => {
      const peer = peersRef.current.get(data.clientId)
      if (peer) peer.selection = data.blockId
      window.dispatchEvent(new CustomEvent('bst:selection', { detail: data }))
    })

    socket.on('block:update', (data: { clientId: string; sectionId: string; blockId: string; data: unknown }) => {
      const state = store.getState()
      const section = state.page.sections.find(s => s.id === data.sectionId)
      if (!section) return
      const block = section.blocks.find(b => b.id === data.blockId)
      if (!block) return
      isApplyingRemote.current = true
      state.updateBlock(data.sectionId, data.blockId, data.data as Record<string, unknown>)
      // Update prevPageRef so subscriber doesn't re-broadcast this remote change
      prevPageRef.current = store.getState().page
      isApplyingRemote.current = false
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [siteId, store])

  return { broadcastBlockUpdate, broadcastCursor, broadcastSelection, peers }
}
